import {
    useMemo,
    ForwardedRef,
    forwardRef,
    useEffect,
    useState,
    useRef,
} from "react";

import { EntityId } from "@reduxjs/toolkit";

import { Box, Grid } from "@mui/material";

import { useAppSelector } from "app/hooks";
import {
    selectShowSidebar,
    selectInFullscreenMode,
    selectInWindowMode,
    selectHideNavigationalElements,
} from "features/core/store/selectors";
import { liveStreamingSelectors } from "./store/selectors";
import { SelectedDeviceProperties } from "features/liveStreaming/types/SelectedDeviceProperties";
import { LiveStreamPlayer } from "./LiveStreamPlayer";
import { LiveStreamingDevicePagination } from "./LiveStreamingDevicePagination";
import { enqueueSnackbar } from "notistack";
import { SiteAppBar } from "./SiteAppBar";
import { selectSiteById } from "features/sites/store/selectors";
import { selectDeviceById } from "features/devices/store/selectors";
import { userSelectors } from "features/user/store/selectors";

export interface LiveStreamingPlayerGridProps {
    siteId: EntityId;
    selectedDevices?: {
        deviceId: EntityId;
        properties: SelectedDeviceProperties;
    }[];
    refreshIntervalInMsUnstaffed: number;
    schedulesPollingIntervalInMs: number;
    minutesBackInTimeForBadConnection: number;
    onCloseSite: (siteId: EntityId) => void;
}

const hightCardActionBar = 40;
const bottomSpacingEdgeScreen = 20;

export const LiveStreamingPlayerGrid = forwardRef<
    HTMLElement,
    LiveStreamingPlayerGridProps
>(
    (
        props: LiveStreamingPlayerGridProps,
        appBarRef: ForwardedRef<HTMLElement>,
    ) => {
        const gridRef = useRef<HTMLDivElement>(null);
        const [marginToTop, setMarginToTop] = useState<number>(0);

        const inWindowMode = useAppSelector(selectInWindowMode);
        const inFullscreenMode = useAppSelector(selectInFullscreenMode);
        const hideNavigationalElements = useAppSelector(
            selectHideNavigationalElements,
        );

        const showSidebar = useAppSelector(selectShowSidebar);
        const fullscreenDeviceId = useAppSelector(
            liveStreamingSelectors.selectFullscreenDeviceId,
        );
        const fullscreenDevice = useAppSelector((state) =>
            selectDeviceById(state, (fullscreenDeviceId || -1).toString()),
        );

        const isSiteVisibleInFullscreen = useMemo(
            () =>
                props.selectedDevices?.some(
                    (x) => x.deviceId === fullscreenDeviceId,
                ) || false,
            [fullscreenDeviceId, props.selectedDevices],
        );

        const hasBadNetworkConnection = useAppSelector((state) =>
            liveStreamingSelectors.selectHasBadNetworkConnection(
                state,
                props.minutesBackInTimeForBadConnection,
            ),
        );
        const [snackBarShouldBeShown, setSnackBarShouldBeShown] =
            useState(true);

        const site = useAppSelector((state) =>
            selectSiteById(state, props.siteId.toString()),
        );

        const audioEnabledByDefault = useAppSelector(
            userSelectors.selectAudioEnabledByDefault,
        );

        const numberOfDevicesPerRow = useAppSelector(
            liveStreamingSelectors.selectNumberOfDevicesPerRow,
        );
        const defaultLiveStreamFeedType = useAppSelector(
            liveStreamingSelectors.selectDefaultLiveStreamFeedType,
        );

        // Show snackbar when bad network connection is detected
        useEffect(() => {
            if (hasBadNetworkConnection && snackBarShouldBeShown) {
                enqueueSnackbar("Bad network connection detected", {
                    variant: "warning",
                    autoHideDuration: 5000,
                });

                setSnackBarShouldBeShown(false);
                setTimeout(
                    () => setSnackBarShouldBeShown(true),
                    props.minutesBackInTimeForBadConnection * 60 * 1000,
                );
            }
        }, [hasBadNetworkConnection, snackBarShouldBeShown]);

        useEffect(() => {
            if (gridRef.current) {
                const rect = gridRef.current.getBoundingClientRect();

                // rect.top is the distance from the top of the viewport in pixels
                setMarginToTop(rect.top);
            }
        }, [gridRef.current]);

        return (
            <>
                {site && (
                    <Box
                        display={
                            inFullscreenMode && !isSiteVisibleInFullscreen
                                ? "none"
                                : "block"
                        }
                    >
                        <SiteAppBar
                            appBarRef={appBarRef}
                            selectedDevices={props.selectedDevices}
                            refreshIntervalInMsUnstaffed={
                                props.refreshIntervalInMsUnstaffed
                            }
                            schedulesPollingIntervalInMs={
                                props.schedulesPollingIntervalInMs
                            }
                            onCloseSite={props.onCloseSite}
                            site={site}
                            hideNavigationalElements={hideNavigationalElements}
                            showSidebar={showSidebar}
                            inWindowMode={inWindowMode}
                            inFullscreenMode={inFullscreenMode}
                            fullscreenDevice={fullscreenDevice}
                        />

                        {inWindowMode && (
                            <LiveStreamingDevicePagination
                                siteId={props.siteId}
                                key={`${props.siteId}-pagination`}
                            />
                        )}

                        <Grid
                            container
                            spacing={inWindowMode ? 2 : 0}
                            ref={gridRef}
                        >
                            {props.selectedDevices?.map((selectedDevice) => (
                                <Grid
                                    size={{
                                        xs: 12 / numberOfDevicesPerRow,
                                    }}
                                    width={
                                        numberOfDevicesPerRow === 1 &&
                                        !inFullscreenMode
                                            ? `calc((100vh - ${marginToTop}px - ${hightCardActionBar}px) * (16/9) - ${bottomSpacingEdgeScreen}px)`
                                            : undefined
                                    }
                                    height={
                                        inFullscreenMode ? "100vh" : undefined
                                    }
                                    key={`${props.siteId}-${selectedDevice.deviceId}`}
                                    display={
                                        inFullscreenMode &&
                                        selectedDevice.deviceId !==
                                            fullscreenDeviceId
                                            ? "none"
                                            : "block"
                                    }
                                >
                                    <LiveStreamPlayer
                                        siteId={props.siteId}
                                        deviceId={selectedDevice.deviceId}
                                        streamQuality={
                                            selectedDevice.properties
                                                .streamQuality
                                        }
                                        enableAudio={
                                            inFullscreenMode &&
                                            selectedDevice.deviceId ===
                                                fullscreenDeviceId
                                        }
                                        audioEnabledByDefault={
                                            audioEnabledByDefault
                                        }
                                        defaultLiveStreamFeedType={
                                            defaultLiveStreamFeedType
                                        }
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </>
        );
    },
);
